import { api } from "aldoo-ra/API"
const reactionsAPI = api("reactions")

class Reactions {
    static instance = null;
    static BATCH_DELAY = 100; // Reduced delay for more responsive handling

    constructor() {
        this.pendingRequests = new Map();
        this.timeoutId = null;
    }

    static getInstance() {
        if (!this.instance) {
            this.instance = new Reactions();
        }
        return this.instance;
    }

    async getStatus(options) {
        // Verify required fields are present
        if (!options.contentId || !options.identifier) {
            return {
                error: 'Missing required fields',
                contentId: options.contentId || null,
                identifier: options.identifier || null,
                counts: {},
                reaction: null
            };
        }

        // Always batch all requests, even if it's the first one and only one
        return new Promise((resolve, reject) => {
            // Create a unique key for this request
            const requestKey = `${options.contentId}-${options.identifier}-${options.reaction || 'all'}`;

            // Create a request object with all the data and the resolve callback
            const requestObj = {
                ...options,
                resolve,
                reject
            };

            // Check if we already have a pending request with this key
            if (this.pendingRequests.has(requestKey)) {
                // Get the existing array of requests
                const requests = this.pendingRequests.get(requestKey);
                // Add the new request to the array
                requests.push(requestObj);
            } else {
                // Create a new array with this request
                this.pendingRequests.set(requestKey, [requestObj]);
            }

            // Clear existing timeout
            if (this.timeoutId) {
                clearTimeout(this.timeoutId);
            }

            // Set new timeout
            this.timeoutId = setTimeout(() => {
                this.processBatch();
            }, Reactions.BATCH_DELAY);
        });
    }

    async processBatch() {
        if (this.pendingRequests.size === 0) return;

        // Group requests by reaction type
        const requestsByReaction = new Map();
        requestsByReaction.set(null, []); // For general requests with no specific reaction

        // Flatten all request arrays and sort them by reaction type
        for (const requestArray of this.pendingRequests.values()) {
            for (const request of requestArray) {
                const reaction = request.reaction || null;

                if (!requestsByReaction.has(reaction)) {
                    requestsByReaction.set(reaction, []);
                }

                requestsByReaction.get(reaction).push(request);
            }
        }

        // Clear pending requests
        const pendingRequests = new Map(this.pendingRequests);
        this.pendingRequests.clear();

        try {
            // Process each group of requests by reaction type
            for (const [reaction, requests] of requestsByReaction.entries()) {
                if (requests.length > 0) {
                    await this.processBulkRequests(requests, reaction);
                }
            }
        } catch (error) {
            // Resolve all pending requests with error
            for (const requestArray of pendingRequests.values()) {
                for (const request of requestArray) {
                    request.resolve({
                        error: error.message || "Failed to process request",
                        contentId: request.contentId,
                        identifier: request.identifier,
                        counts: {},
                        reaction: null
                    });
                }
            }
        }
    }

    async processBulkRequests(requests, reaction) {
        if (requests.length === 0) return;

        // Group requests by contentId and identifier to avoid duplicates
        const uniqueRequests = new Map();
        const allRequests = new Map();

        // Build maps of unique requests and all requests
        requests.forEach(request => {
            const key = `${request.contentId}-${request.identifier}`;

            // Store all requests by key
            if (!allRequests.has(key)) {
                allRequests.set(key, []);
            }
            allRequests.get(key).push(request);

            // Only include one request per unique key in the API call
            uniqueRequests.set(key, request);
        });

        // Create batch of requests with just the unique ones
        const batch = Array.from(uniqueRequests.values()).map(req => {
            const batchItem = {
                contentId: req.contentId,
                identifier: req.identifier,
                isAnonymous: req.isAnonymous
            };

            // Only include reaction if specified
            if (reaction) {
                batchItem.reaction = reaction;
            }

            // Include contentType if provided
            if (req.contentType) {
                batchItem.contentType = req.contentType;
            }

            return batchItem;
        });

        try {
            // Create the API request object
            const apiRequest = {
                status: {
                    requests: batch
                }
            };

            // Include reaction parameter for specific reaction requests
            if (reaction) {
                apiRequest.status.reaction = reaction;
            }

            // Make a single API call for all unique requests
            const response = await reactionsAPI(apiRequest);

            if (response.error) {
                // Handle batch error - resolve all requests
                for (const requestArray of allRequests.values()) {
                    for (const request of requestArray) {
                        const errorResponse = {
                            error: response.error,
                            contentId: request.contentId,
                            identifier: request.identifier
                        };

                        // Add appropriate defaults based on request type
                        if (reaction) {
                            errorResponse.reaction = null;
                            errorResponse.hasReaction = false;
                            errorResponse.count = 0;
                        } else {
                            errorResponse.counts = {};
                            errorResponse.reaction = null;
                        }

                        request.resolve(errorResponse);
                    }
                }
                return;
            }

            // Check if response has results
            if (!response.results || !Array.isArray(response.results)) {
                // Handle invalid response - resolve all requests
                for (const requestArray of allRequests.values()) {
                    for (const request of requestArray) {
                        const errorResponse = {
                            error: "Invalid response format",
                            contentId: request.contentId,
                            identifier: request.identifier
                        };

                        // Add appropriate defaults based on request type
                        if (reaction) {
                            errorResponse.reaction = null;
                            errorResponse.hasReaction = false;
                            errorResponse.count = 0;
                        } else {
                            errorResponse.counts = {};
                            errorResponse.reaction = null;
                        }

                        request.resolve(errorResponse);
                    }
                }
                return;
            }

            // Create a lookup for the response results
            const resultsByKey = new Map();
            response.results.forEach(result => {
                if (result.contentId && result.identifier) {
                    const key = `${result.contentId}-${result.identifier}`;
                    resultsByKey.set(key, result);
                }
            });

            // Distribute results to all registered callbacks
            for (const [key, requestArray] of allRequests.entries()) {
                const result = resultsByKey.get(key);

                if (result) {
                    // Resolve all requests with this key using the same result
                    for (const request of requestArray) {
                        request.resolve(result);
                    }
                } else {
                    // Handle requests that didn't get a result
                    for (const request of requestArray) {
                        const defaultResponse = {
                            contentId: request.contentId,
                            identifier: request.identifier
                        };

                        // Add appropriate defaults based on request type
                        if (reaction) {
                            defaultResponse.reaction = null;
                            defaultResponse.hasReaction = false;
                            defaultResponse.count = 0;
                        } else {
                            defaultResponse.counts = {};
                            defaultResponse.reaction = null;
                        }

                        request.resolve(defaultResponse);
                    }
                }
            }
        } catch (error) {
            // Handle network error - resolve all requests with appropriate defaults
            for (const requestArray of allRequests.values()) {
                for (const request of requestArray) {
                    const errorResponse = {
                        error: error.message || "Request failed",
                        contentId: request.contentId,
                        identifier: request.identifier
                    };

                    // Add appropriate defaults based on request type
                    if (reaction) {
                        errorResponse.reaction = null;
                        errorResponse.hasReaction = false;
                        errorResponse.count = 0;
                    } else {
                        errorResponse.counts = {};
                        errorResponse.reaction = null;
                    }

                    request.resolve(errorResponse);
                }
            }
        }
    }

    async toggleReaction(options) {
        // Verify required fields
        if (!options.contentId || !options.identifier) {
            return {
                error: 'Missing required fields'
            };
        }

        try {
            const payload = {
                reaction: {
                    contentId: options.contentId,
                    identifier: options.identifier,
                    isAnonymous: options.isAnonymous,
                    reaction: options.reaction || 'like'
                }
            };

            // Include contentType if provided
            if (options.contentType) {
                payload.reaction.contentType = options.contentType;
            }

            const response = await reactionsAPI(payload);

            return response;
        } catch (error) {
            return {
                error: error.message || "Failed to process reaction"
            };
        }
    }

    async removeReaction(options) {
        // Verify required fields
        if (!options.contentId || !options.identifier) {
            return {
                error: 'Missing required fields'
            };
        }

        try {
            const response = await reactionsAPI({
                unreact: {
                    contentId: options.contentId,
                    identifier: options.identifier,
                    reaction: options.reaction || 'like'
                }
            });

            return response;
        } catch (error) {
            return {
                error: error.message || "Failed to process unreact"
            };
        }
    }

    // Get all reaction counts for a content
    async getReactionCounts(options) {
        const contentId = typeof options === 'string' ? options : options.contentId;

        if (!contentId) {
            return {};
        }

        try {
            // Use status with a dummy request
            const response = await reactionsAPI({
                status: {
                    requests: [{
                        contentId,
                        identifier: "counts-only" // Use a dummy identifier
                    }]
                }
            });

            if (response.error) {
                return {};
            }

            // Get the first result
            if (response.results && response.results.length > 0) {
                return response.results[0].counts || {};
            }

            return {};
        } catch (error) {
            return {};
        }
    }
}

export default Reactions;